import { createApi } from '@reduxjs/toolkit/query/react';

import { generateQueryStringToGetTotalNumberOfProductsInCategory } from '@/helpers/utils/qs';
import { CategoryStrapi, CategoryWiseProductsResponse } from '@/types/Category';
import { ProductStrapi } from '@/types/Product';

import { flatMapProductsAdapter } from '../adapters';
import { generateBaseQuseryWithReAuth } from '../baseQueryReauth';
import { setCategoryDetails } from '../slices/categoryDetails';

export const categoryApi = createApi({
  reducerPath: 'categoryApi',
  keepUnusedDataFor: 300,
  baseQuery: generateBaseQuseryWithReAuth({ useStrapiServer: true, basePath: '/api/categories', cache: 'force-cache' }),
  tagTypes: ['Category', 'NumberOfProductsInCategory'],
  endpoints: builder => ({
    getCategoryById: builder.query<
      {
        categoryDetailResponse: { data: CategoryWiseProductsResponse } | undefined;
        categoryDetailData:
          | {
              Products: ProductStrapi[];
              Category: CategoryStrapi;
            }
          | undefined;
        products: ProductStrapi[];
      },
      { categoryId: string | number; itemCodes: string[] }
    >({
      query: ({ categoryId, itemCodes }) => {
        const itemCodesQuery = itemCodes.length ? `?itemCodes=${itemCodes.toString()}` : '';
        const urlWithoutQuery = `/shopping-experience/${categoryId}`;
        const url = `${urlWithoutQuery}${itemCodesQuery}`;

        return {
          url,
          next: { tags: ['Category'] },
        };
      },
      providesTags: ['Category'],
      transformResponse(categoryDetailResponse: { data: CategoryWiseProductsResponse }, meta, arg) {
        const categoryDetailData = categoryDetailResponse
          ? flatMapProductsAdapter(categoryDetailResponse.data)
          : categoryDetailResponse;
        const products = categoryDetailData?.Products ?? ([] as ProductStrapi[]);

        return { categoryDetailResponse, categoryDetailData, products };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        const { categoryDetailResponse } = data || {};

        if (categoryDetailResponse?.data) dispatch(setCategoryDetails(categoryDetailResponse.data));
      },
    }),

    getNumberOfTotalProductsInCategory: builder.query<number, { categoryId: string | number }>({
      query: ({ categoryId }) => {
        return {
          url: `/${categoryId}?${generateQueryStringToGetTotalNumberOfProductsInCategory}`,
          next: { tags: ['NumberOfProductsInCategory'] },
        };
      },
      providesTags: ['NumberOfProductsInCategory'],
      transformResponse(categoryResp: { data: CategoryWiseProductsResponse }, meta, arg) {
        const thisCategoryProducts = categoryResp?.data?.attributes?.Products?.data || [];
        const subCategoriesProducts =
          categoryResp?.data?.attributes?.SubCategories?.data?.flatMap(
            category => category?.attributes?.Products?.data || []
          ) || [];

        const allProducts = [...thisCategoryProducts, ...subCategoriesProducts].map(
          product => product?.attributes?.ItemCode
        );
        const uniqueProducts = new Set(allProducts);

        return uniqueProducts.size;
      },
    }),
  }),
});

export const { useGetCategoryByIdQuery, useGetNumberOfTotalProductsInCategoryQuery } = categoryApi;
